import {
  ButtonERP,
  CalendarPrimeReact,
  DropdownPrimeReactNew,
} from "@finanedi.es/storybook"
import {
  postAsientoContable,
  postContraasiento,
  putAsientoContable,
} from "api/AsientoContable.service"
import { GetAsientoContableByIdDtoProps } from "api/DTOs/GetAsientoContableByIdDto"
import { LineaAsientoGetAsientoContableByIdDtoProps } from "api/DTOs/LineaAsientoGetAsientoContableByIdDto"
import { LineaImpuestoGetAsientoContableByIdDtoProps } from "api/DTOs/LineaImpuestoGetAsientoContableByIdDto"
import { SHOW_IMPUESTOS_TABLE } from "api/Types/TipoAsiento"
import { TIPO_IMPORTACION_ASIENTO } from "api/Types/TipoImportacionAsiento"
import { TIPO_LINEA_ASIENTO } from "api/Types/TipoLineaAsiento"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import { formatCurrency } from "common/utils/currency"
import notification from "common/utils/notification"
import { TIPOS_ASIENTOS } from "features/Contabilidad/pages/LibroMayor/constants/TiposAsientos"
import { PRIME_REACT_MODAL_DEFAULT_PROPS } from "features/Tesoreria/pages/Bancos/components/AfterBanksForm/src/_constants"
import { FormikHandlers, FormikHelpers, withFormik } from "formik"
import { TFunction } from "i18next"
import { FormikErrors } from "interfaces/Formik"
import { InputText } from "primereact/inputtext"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { backendDateFormat } from "utils/formatDate"
import { precisionNumber } from "utils/precisionNumber"
import { v4 as uuidv4 } from "uuid"
import LineasAsientoContable from "./Components/LineasAsientoContable/LineasAsientoContable"
import LineasImpuestosAsientosContable from "./Components/LineasImpuestosAsientosContable/LineasImpuestosAsientosContable"
import ModalContraAsiento from "./Components/ModalContraAsiento/ModalContraAsiento"
import {
  AsientoContableFormikProps,
  INITIAL_VALUES_DEFAULT,
  LineaAsientoFormikProps,
  LineaImpuestoFormikProps,
  adapterPostAsientoContable,
  adapterPutAsientoContable,
  validationSchemaAsientosFormik,
} from "./constant"

const FormAsientoContable = ({
  setFieldValue,
  values,
  errors,
  dirty,
  onHideModal,
  updateTable,
  handleSubmit,
  isSubmitting,
  asientoDto,
}: {
  setFieldValue: FormikHelpers<AsientoContableFormikProps>["setFieldValue"]
  values: AsientoContableFormikProps
  errors: FormikErrors<AsientoContableFormikProps>
  dirty: boolean
  onHideModal: () => void
  updateTable: (responseData?: string) => void
  handleSubmit: FormikHandlers["handleSubmit"]
  isSubmitting: boolean
  asientoDto: GetAsientoContableByIdDtoProps | undefined
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [loadingContrasiento, setLoadingContrasiento] = useState(false)
  const [loadingDuplicar, setLoadingDuplicar] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [descuadre, setDescuadre] = useState(0)

  const createContraAsiento = async (id: string) => {
    setLoadingContrasiento(true)
    const { status }: { status: number } = await postContraasiento(id)
    if (status === STATUSCORRECT) {
      notification.success({
        body: `${t("notification.successGenerarContrasiento")}`,
      })
      setShowDeleteModal(!showDeleteModal)
      updateTable()
    }
    setLoadingContrasiento(false)
  }

  const duplicarAsiento = async () => {
    setLoadingDuplicar(true)
    if (asientoDto) {
      const duplicateAsiento = {
        fecha: backendDateFormat(new Date(asientoDto.fecha)),
        ficheros: [],
        lineas: asientoDto.lineas
          ? asientoDto.lineas.map((linea) => ({
              cargoAbono: linea.cargoAbono,
              cuentaContableId: linea.cuentaContableId,
              descripcion: linea.descripcion,
              importe: linea.importe,
              numeroDocumento: linea.numeroDocumento,
            }))
          : null,
        lineasImpuesto: asientoDto.lineasImpuesto,
        notas: asientoDto.notas,
        tipoAsiento: asientoDto.tipoAsiento,
      }
      const { status }: { status: number } = await postAsientoContable(
        duplicateAsiento
      )
      if (status === STATUSCORRECT) {
        notification.success({
          body: `${t("cuadroCuentas.msg.duplicateEntryExit")}`,
        })
        onHideModal()
        updateTable()
      }
    }
    setLoadingDuplicar(false)
  }

  const calculateDescuadre = () => {
    const totalDebe = values.lineas.reduce(
      (acc: number, linea: LineaAsientoFormikProps) =>
        acc + Number(linea.debe ?? 0),
      0
    )
    const totalHaber = values.lineas.reduce(
      (acc: number, linea: LineaAsientoFormikProps) =>
        acc + Number(linea.haber ?? 0),
      0
    )
    const descuadre = precisionNumber(totalDebe - totalHaber)
    setDescuadre(descuadre)
  }

  useEffect(() => {
    calculateDescuadre()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.lineas])

  return (
    <div className='tw-w-full tw-h-full tw-flex tw-flex-col'>
      <div className='tw-flex tw-flex-row tw-w-full tw-gap-4'>
        <div className='tw-flex tw-flex-col tw-w-80'>
          <label className='tw-text-sm'>
            {t("diario.modal.typeEntryLabel")}
          </label>
          <DropdownPrimeReactNew
            options={TIPOS_ASIENTOS(t)}
            optionLabel='descripcion'
            optionValue='tipo'
            panelClassName='!tw-text-sm'
            value={values.tipoAsiento}
            className='!tw-text-text !tw-text-sm !tw-border-neutral-25 hover:!tw-border-neutral-75'
            onChange={({ value }: { value: number }) => {
              setFieldValue("tipoAsiento", value)
              setFieldValue(
                "lineasImpuesto",
                INITIAL_VALUES_DEFAULT.lineasImpuesto
              )
            }}
            disabled={
              values.tipoImportacionAsiento ===
              TIPO_IMPORTACION_ASIENTO.LIBRO_IVA_SAGE
            }
            pt={{
              input: {
                className: "!tw-text-sm ",
              },
              root: {
                className: "tw-h-10",
              },
            }}
          />
        </div>
        <div className='tw-flex tw-flex-col tw-w-52'>
          <label className='tw-text-sm'>{t("diario.modal.dateLabel")}</label>
          <CalendarPrimeReact
            value={values.fecha}
            selectionMode='single'
            locale={language}
            dateFormat='dd/mm/yy'
            onChange={({ value }) => {
              setFieldValue("fecha", value)
            }}
            readOnlyInput
            showIcon
            pt={{
              ...PRIME_REACT_MODAL_DEFAULT_PROPS.calendar,
              root: {
                className: "tw-h-10 [&>:first-child]:!tw-text-sm",
              },
            }}
          />
        </div>
        <div className='tw-flex tw-flex-col tw-flex-1'>
          <label className='tw-text-sm'>{t("diario.modal.noteLabel")}</label>
          <InputText
            value={values.notas ?? ""}
            className='!tw-text-sm !tw-h-10 !tw-border-neutral-25 hover:!tw-border-neutral-75'
            onChange={(e) => setFieldValue("notas", e.target.value)}
          />
        </div>
      </div>
      <div className='tw-flex tw-flex-col tw-h-[calc(100%-68px)]'>
        <div
          className={`tw-flex tw-flex-col ${
            values.tipoImportacionAsiento ===
            TIPO_IMPORTACION_ASIENTO.LIBRO_IVA_SAGE
              ? "tw-h-fit"
              : SHOW_IMPUESTOS_TABLE.includes(values.tipoAsiento)
              ? "tw-h-3/5 tw-max-h-3/5"
              : "tw-h-full"
          }  tw-pt-6`}
        >
          <LineasAsientoContable
            values={values}
            errors={errors}
            setFieldValue={setFieldValue}
          />
          <div
            className={`tw-h-3 tw-text-sm tw-text-danger ${
              values.tipoImportacionAsiento ===
              TIPO_IMPORTACION_ASIENTO.LIBRO_IVA_SAGE
                ? "tw-hidden"
                : ""
            }`}
          >
            {descuadre !== 0 ? (
              <span>
                {t("diario.modal.deductionLabel")}{" "}
                {`${formatCurrency(descuadre)}`}
              </span>
            ) : (
              <span />
            )}
          </div>

          <div className='tw-flex tw-flex-row tw-pt-3 tw-w-full tw-justify-end tw-items-end'>
            <div className='tw-h-10 tw-flex tw-flex-row tw-gap-4'>
              <ButtonERP
                customize={{
                  type: "white",
                  size: "md",
                }}
                onClick={() => {
                  onHideModal()
                }}
                label={`${t("diario.modal.cancelButton")}`}
              />
              {values.isEdit && (
                <div className='tw-flex tw-flex-row tw-gap-4'>
                  <ButtonERP
                    customize={{
                      type: "danger",
                      size: "md",
                    }}
                    onClick={() => setShowDeleteModal(!showDeleteModal)}
                    label={`${t("diario.modal.contraAsientoButton")}`}
                  />
                  <ButtonERP
                    loading={loadingDuplicar}
                    onClick={() => duplicarAsiento()}
                    disabled={dirty}
                    customize={{
                      type: "primary",
                      size: "md",
                    }}
                    label={`${t("diario.modal.duplicateButton")}`}
                  />
                </div>
              )}

              <ButtonERP
                loading={isSubmitting}
                onClick={() => {
                  handleSubmit()
                }}
                customize={{
                  type: "primary",
                  size: "md",
                }}
                label={
                  values.isEdit
                    ? `${t("diario.modal.saveButton")}`
                    : `${t("diario.modal.createButton")}`
                }
              />
            </div>
          </div>
        </div>
        {SHOW_IMPUESTOS_TABLE.includes(values.tipoAsiento) && (
          <div
            className={`${
              values.tipoImportacionAsiento ===
              TIPO_IMPORTACION_ASIENTO.LIBRO_IVA_SAGE
                ? "tw-h-[calc(100%-76px)]"
                : "tw-h-2/5 tw-max-h-2/5"
            }`}
          >
            <LineasImpuestosAsientosContable
              values={values}
              errors={errors}
              setFieldValue={setFieldValue}
            />
          </div>
        )}
      </div>
      <ModalContraAsiento
        visible={showDeleteModal}
        isloadingContrasiento={loadingContrasiento}
        onCancel={() => setShowDeleteModal(!showDeleteModal)}
        onConfirm={() => values.id && createContraAsiento(values.id)}
      />
    </div>
  )
}

const FormAsientoFormik = withFormik({
  validateOnMount: false,
  validateOnChange: false,
  validateOnBlur: false,
  mapPropsToValues: (props: {
    onHideModal: (refetch?: boolean) => void
    data: GetAsientoContableByIdDtoProps | undefined
    updateTable: (responseData?: string) => void
    t: TFunction
    asientoDto: GetAsientoContableByIdDtoProps | undefined
    preData: Partial<AsientoContableFormikProps> | undefined
  }): AsientoContableFormikProps => {
    return props.data
      ? {
          isEdit: true,
          id: props.data.id,
          fecha: new Date(props.preData?.fecha ?? props.data.fecha),
          ficheros: props.data.ficheros ?? [],
          lineas: props.data.lineas
            ? props.data.lineas.map(
                (
                  linea: LineaAsientoGetAsientoContableByIdDtoProps
                ): LineaAsientoFormikProps => {
                  return {
                    id: linea?.id,
                    cuentaContableId: linea?.cuentaContableId,
                    numeroDocumento: linea?.numeroDocumento ?? "",
                    descripcion: linea?.descripcion ?? "",
                    listadoTags: linea?.listadoTags ?? [],
                    debe:
                      linea?.cargoAbono === TIPO_LINEA_ASIENTO.DEBE
                        ? linea?.importe
                        : null,
                    haber:
                      linea?.cargoAbono === TIPO_LINEA_ASIENTO.HABER
                        ? linea?.importe
                        : null,
                  }
                }
              )
            : INITIAL_VALUES_DEFAULT.lineas,
          lineasImpuesto: props.data.lineasImpuesto?.length
            ? props.data.lineasImpuesto.map(
                (
                  impuesto: LineaImpuestoGetAsientoContableByIdDtoProps
                ): LineaImpuestoFormikProps => ({
                  categoria: impuesto.categoria,
                  id: uuidv4(),
                  claveImpuesto: impuesto.claveImpuesto,
                  porcentajeImpuesto: impuesto.porcentajeImpuesto,
                  cuentaContableId: impuesto.cuentaContableId,
                  subtotal: impuesto.subtotal,
                  valorImpuesto: impuesto.valorImpuesto,
                })
              )
            : INITIAL_VALUES_DEFAULT.lineasImpuesto,
          notas: props.data.notas,
          numero: props.data.numero,
          tipoAsiento: props.data.tipoAsiento,
          tipoImportacionAsiento: props.data.tipoImportacionAsiento,
          descuadre: 0,
        }
      : props.preData
      ? { ...INITIAL_VALUES_DEFAULT, ...props.preData }
      : INITIAL_VALUES_DEFAULT
  },
  enableReinitialize: false,
  validationSchema: ({ t }: { t: TFunction }) =>
    validationSchemaAsientosFormik(t),
  handleSubmit: async (values, { props, resetForm }) => {
    if (values.isEdit) {
      const dataPutFormated = adapterPutAsientoContable(values)
      const { status }: { status: number } = await putAsientoContable(
        dataPutFormated
      )
      if (status === STATUSCORRECT) {
        notification.success({
          body: `${props.t("cuadroCuentas.msg.entryUpdateExit")}`,
        })
        props.onHideModal(true)
        props.updateTable()
      }
    } else {
      const dataPostFormated = adapterPostAsientoContable(values)
      const { status, data }: { status: number; data: string } =
        await postAsientoContable(dataPostFormated)
      if (status === STATUSCORRECT) {
        resetForm()
        notification.success({
          body: `${props.t("cuadroCuentas.msg.addEntryExit")}`,
        })
        props.onHideModal()
        props.updateTable(data)
      }
    }
  },
})(FormAsientoContable)

export default FormAsientoFormik
